import type { SignupSchema } from '@repo/schemas/auth/signup';
import type { UserPrivate } from '@repo/types/users';
import type { UseMutateAsyncFunction } from '@tanstack/react-query';

import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { signupSchema, signupSchemaParser } from '@repo/schemas/auth/signup';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';

import { useUpdateCredentials } from '@/store/auth-store';
import { getAvatar } from '@/utils/getAvatar';

export function useSignupForm(mutateAsync: UseMutateAsyncFunction<string, Error, SignupSchema, unknown>, userData?: UserPrivate, authToken?: string) {
  const navigate = useNavigate();
  const updateCredentials = useUpdateCredentials();

  const form = useForm<SignupSchema>({
    defaultValues: {
      authToken: authToken,
      email: '',
      phone: null,
      birthdate: null,
    },
    mode: 'onSubmit',
    resolver: standardSchemaResolver(signupSchema),
  });

  const onSubmit = async (formData: SignupSchema) => {
    // The error is surfaced through usePostSignup's `error`/`errorMessage`
    // state that drives the Alert in SignupForm, so we explicitly swallow
    // the rejection here to avoid an unhandled-rejection warning.
    await mutateAsync(formData)
      .then(() => {
        updateCredentials({
          email: formData.email,
          phone: formData.phone ?? undefined,
          birthdate: formData.birthdate ?? undefined,
        });
        navigate(`/auth/passwordChange?t=${authToken}`);
      })
      .catch(() => {});
  };

  useEffect(() => {
    if (!userData) return;
    updateCredentials({ avatarURL: getAvatar(userData.avatarURL) });
    const parsed = signupSchemaParser.safeParse({ authToken, ...userData });
    if (parsed.success) {
      form.reset(parsed.data);
    }
  }, [userData]);

  return {
    form: form,
    onSubmit: onSubmit,
  };
}
